import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import { BaseFilter } from 'ag-grid-community';
import { UserGroupService } from '../api-users/api/userGroup.service';
import { UserGroupsV2Service } from '../api-users/api/userGroupsV2.service';
import { UserGroupResource } from '../api-users/model/userGroupResource';
import { UserGroupRequest } from '../api-users/model/userGroupRequest';
import { AssignUsersToGroupRequest } from '../api-users/model/assignUsersToGroupRequest';
import { AppHttpResourcesV2UserGroupsUserGroupResource } from '../api-users/model/appHttpResourcesV2UserGroupsUserGroupResource';
import { V2UserGroupsUserGroupIndexFilterParameter } from '../api-users/model/v2UserGroupsUserGroupIndexFilterParameter';
import { V2UserGroupsUserGroupIndex200Response } from '../api-users/model/v2UserGroupsUserGroupIndex200Response';

@Injectable({
    providedIn: 'root',
})
export class GroupsService {
    private _group: BehaviorSubject<UserGroupResource | null> = new BehaviorSubject<UserGroupResource | null>(null);
    private _groups: BehaviorSubject<UserGroupResource[] | null> = new BehaviorSubject<UserGroupResource[] | null>(null);

    public actualFilter?: BaseFilter;

    constructor(
        private readonly _groupsDsService: UserGroupService,
        private readonly _groupsV2Service: UserGroupsV2Service
    ) {}

    get groups$(): Observable<UserGroupResource[] | null> {
        return this._groups.asObservable();
    }

    public getGroup(idGroup: number) {
        return this._groupsV2Service.v2UserGroupsUserGroupShow(idGroup, 'companies,users').pipe(
            map((group: AppHttpResourcesV2UserGroupsUserGroupResource) => {
                return this.mapV2GroupToUserGroupResource(group);
            }),
            tap(group => {
                this._group.next(group);
            }),
        );
    }

    public getGroups() {
        return this._groupsV2Service.v2UserGroupsUserGroupIndex(1, 100, undefined, undefined, 'companies,usersCount', undefined).pipe(
            map(response => {
                const mappedGroups = response.data.map(group => this.mapV2GroupToUserGroupResource(group));
                return { data: mappedGroups };
            }),
            tap((groups: any) => {
                this._groups.next(groups.data);
            }),
        );
    }

    public getGroupsWithUsers() {
        return this._groupsV2Service.v2UserGroupsUserGroupIndex(1, 100, undefined, undefined, 'companies,usersCount', undefined).pipe(
            map(response => {
                const mappedGroups = response.data.map(group => this.mapV2GroupToUserGroupResource(group));
                return { data: mappedGroups };
            }),
        );
    }

    public getGroupsPaginated(
        page: number,
        perPage: number,
        filter?: Partial<V2UserGroupsUserGroupIndexFilterParameter>
    ): Observable<V2UserGroupsUserGroupIndex200Response> {
        return this._groupsV2Service.v2UserGroupsUserGroupIndex(
            page,
            perPage,
            undefined,
            undefined,
            'companies,usersCount',
            filter as V2UserGroupsUserGroupIndexFilterParameter | undefined
        );
    }

    searchGroups(baseFilter: any): Observable<any> {
        this.actualFilter = baseFilter;

        const page = baseFilter.page || 1;
        const perPage = baseFilter.perPage || 20;

        let sort: string | undefined;
        if (baseFilter.sort && baseFilter.direction) {
            const direction = baseFilter.direction === 'asc' ? '' : '-';
            sort = `${direction}${baseFilter.sort}`;
        }

        const filter: Partial<V2UserGroupsUserGroupIndexFilterParameter> = {};
        if (baseFilter.name) {
            filter.name = baseFilter.name;
        }
        if (baseFilter.type) {
            filter.type = baseFilter.type;
        }

        return this._groupsV2Service.v2UserGroupsUserGroupIndex(
            page,
            perPage,
            undefined,
            sort,
            'companies,usersCount',
            Object.keys(filter).length > 0 ? filter as V2UserGroupsUserGroupIndexFilterParameter : undefined
        ).pipe(
            map(response => {
                const mappedGroups = response.data.map(group => this.mapV2GroupToUserGroupResource(group));
                return { data: mappedGroups, meta: response.meta };
            }),
        );
    }

    public updateGroup(editedGroup: number, group: UserGroupRequest): Observable<any> {
        return this._groupsDsService.updateUserGroup(editedGroup, group);
    }

    public createGroup(group: UserGroupRequest): Observable<any> {
        return this._groupsDsService.createUserGroup(group);
    }

    public assignUsersToGroup(groupId: number, userIds: number[]): Observable<any> {
        const request: AssignUsersToGroupRequest = { userIds };
        return this._groupsDsService.assignUsersToGroup(groupId, request);
    }

    public removeUserFromGroup(groupId: number, userId: number): Observable<any> {
        return this._groupsDsService.removeUserFromGroup(groupId, {userIds: [userId]});
    }

    public removeUsersFromGroup(groupId: number, userIds: number[]): Observable<any> {
        return this._groupsDsService.removeUserFromGroup(groupId, {userIds: userIds});
    }

    public deleteGroup(groupId: number): Observable<any> {
        return this._groupsDsService.deleteUserGroup(groupId);
    }

    private mapV2GroupToUserGroupResource(v2Group: AppHttpResourcesV2UserGroupsUserGroupResource): UserGroupResource {
        return {
            id: v2Group.id,
            name: v2Group.name,
            description: v2Group.description,
            type: v2Group.type,
            companies: v2Group.companies,
            users: v2Group.users?.map(user => ({
                id: user.id,
                firstname: user.firstname,
                lastname: user.lastname,
                email: user.email,
                fullname: user.fullname
            } as any)),
            usersCount: v2Group.usersCount?.toString() || '0'
        } as UserGroupResource;
    }

}
